import { useCallback, useEffect, useState } from 'react';

import { LANGS, LANG_CODES, LANG_NAMES, type ConceptPrefs, type Lang } from '@lexio/shared';

import { ConceptEditor } from '../components/ConceptEditor';
import { listConcepts, listTopics, setPrefs, type ConceptListParams, type ConceptWithPrefs } from '../lib/api';

const FILTERS: { value: NonNullable<ConceptListParams['filter']>; label: string }[] = [
  { value: 'all', label: 'Vše' },
  { value: 'unseen', label: 'Nezkoušené' },
  { value: 'prioritized', label: 'Prioritní' },
  { value: 'known', label: 'Už umím' },
  { value: 'suppressed', label: 'Vyřazené' },
];

/** Ceske sklonovani po cisle. */
function formatCount(n: number): string {
  if (n === 1) return '1 položka';
  if (n >= 2 && n <= 4) return `${n} položky`;
  return `${n} položek`;
}

/**
 * Prohlizec slovni zasoby. Ukazuje koncept ve vsech jazycich najednou,
 * takze je videt, kde chybi preklad.
 */
export function Browse() {
  const [items, setItems] = useState<ConceptWithPrefs[]>([]);
  const [total, setTotal] = useState(0);
  const [topics, setTopics] = useState<{ slug: string; name: string; conceptCount: number }[]>([]);
  const [query, setQuery] = useState('');
  const [topic, setTopic] = useState('');
  const [lang, setLang] = useState<Lang | ''>('');
  const [filter, setFilter] = useState<NonNullable<ConceptListParams['filter']>>('all');
  const [expanded, setExpanded] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void listTopics().then((r) => setTopics(r.topics));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listConcepts({
        q: query || undefined,
        topic: topic || undefined,
        lang: lang || undefined,
        filter,
        limit: 100,
      });
      setItems(res.items);
      setTotal(res.total);
    } finally {
      setLoading(false);
    }
  }, [query, topic, lang, filter]);

  // Debounce hledani, aby se pri psani neposilal dotaz na kazde pismeno.
  useEffect(() => {
    const t = setTimeout(() => void load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  const updatePrefs = async (conceptId: string, patch: Partial<ConceptPrefs>) => {
    setItems((prev) =>
      prev.map((it) => (it.id === conceptId ? { ...it, prefs: { ...it.prefs, ...patch } } : it)),
    );
    try {
      await setPrefs(conceptId, patch);
    } catch {
      void load(); // vratime se k pravde ze serveru
    }
  };

  return (
    <div className="browse">
      <div className="filters">
        <input
          className="input"
          type="search"
          placeholder="Hledat v jakémkoli jazyce…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />

        <div className="chips">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              className={`chip-btn ${filter === f.value ? 'active' : ''}`}
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="filter-row">
          <select className="input" value={topic} onChange={(e) => setTopic(e.target.value)}>
            <option value="">Všechna témata</option>
            {topics.map((t) => (
              <option key={t.slug} value={t.slug}>
                {t.name} ({t.conceptCount})
              </option>
            ))}
          </select>

          <select className="input" value={lang} onChange={(e) => setLang(e.target.value as Lang | '')}>
            <option value="">Všechny jazyky</option>
            {LANGS.map((l) => (
              <option key={l} value={l}>
                má {LANG_NAMES[l]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="muted count">{loading ? 'Načítám…' : formatCount(total)}</div>

      <ul className="entry-list">
        {items.map((c) => {
          const cs = c.terms.cs;
          const en = c.terms.en;
          const isOpen = expanded === c.id;

          return (
            <li key={c.id} className={`entry ${c.prefs.suppressed ? 'suppressed' : ''}`}>
              <div className="entry-head" onClick={() => setExpanded(isOpen ? null : c.id)}>
                <div>
                  <div className="entry-term">{en?.term ?? cs?.term ?? c.key}</div>
                  <div className="entry-trans">{cs?.term}</div>
                </div>
                <div className="entry-badges">
                  <span className="lang-count">{c.langs.length}×</span>
                  {c.prefs.priority !== 0 && (
                    <span className="tag prio">
                      {c.prefs.priority > 0 ? `+${c.prefs.priority}` : c.prefs.priority}
                    </span>
                  )}
                  {c.prefs.known && <span className="tag known">umím</span>}
                  {c.level && <span className="tag">{c.level}</span>}
                </div>
              </div>

              {isOpen && editing === c.id && (
                <div className="entry-detail">
                  <ConceptEditor
                    concept={c}
                    onCancel={() => setEditing(null)}
                    onSaved={(saved) => {
                      setItems((prev) => prev.map((it) => (it.id === saved.id ? { ...it, ...saved } : it)));
                      setEditing(null);
                    }}
                  />
                </div>
              )}

              {isOpen && editing !== c.id && (
                <div className="entry-detail">
                  {/* Vsechny jazyky vedle sebe - hlavni prinos vicejazycneho modelu */}
                  <table className="lang-table">
                    <tbody>
                      {LANGS.filter((l) => c.terms[l]).map((l) => {
                        const t = c.terms[l]!;
                        return (
                          <tr key={l}>
                            <td className="lang-cell">{LANG_CODES[l]}</td>
                            <td>
                              <div className="lang-term">{t.term}</div>
                              {t.variants.length > 0 && (
                                <div className="muted small">{t.variants.join(' · ')}</div>
                              )}
                              {t.note && <div className="muted small note-inline">💡 {t.note}</div>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {c.examples.map((ex, i) => (
                    <div className="example" key={i}>
                      {LANGS.filter((l) => ex[l]).map((l) => (
                        <div className={l === 'cs' ? 'ex-cs' : 'ex-en'} key={l}>
                          <span className="other-code">{LANG_CODES[l]}</span> {ex[l]}
                        </div>
                      ))}
                    </div>
                  ))}

                  <div className="entry-actions">
                    <button className="btn ghost small" onClick={() => setEditing(c.id)}>
                      ✎ Upravit
                    </button>
                    <button
                      className="btn ghost small"
                      onClick={() => void updatePrefs(c.id, { priority: Math.min(c.prefs.priority + 1, 2) })}
                    >
                      ▲ Priorita
                    </button>
                    <button
                      className="btn ghost small"
                      onClick={() => void updatePrefs(c.id, { priority: Math.max(c.prefs.priority - 1, -2) })}
                    >
                      ▼ Priorita
                    </button>
                    <button
                      className={`btn ghost small ${c.prefs.known ? 'active' : ''}`}
                      onClick={() => void updatePrefs(c.id, { known: !c.prefs.known })}
                    >
                      {c.prefs.known ? 'Zrušit „umím“' : 'Už umím'}
                    </button>
                    <button
                      className={`btn ghost small ${c.prefs.suppressed ? 'active' : ''}`}
                      onClick={() => void updatePrefs(c.id, { suppressed: !c.prefs.suppressed })}
                    >
                      {c.prefs.suppressed ? 'Vrátit zpět' : 'Vyřadit'}
                    </button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>

      {!loading && items.length === 0 && <div className="center muted">Nic nenalezeno.</div>}
    </div>
  );
}
